import React from 'react';
import PropTypes from 'prop-types';
import TextField from '../TextField';
import useArrayField from './useArrayField';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useRegisterInput from '../Form/useRegisterInput';
import { useFormContext } from '../Form';
import ErrorMessage from '../ErrorMessage';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import translations from './translations';
import Icon from '../Icon';
import styled from 'styled-components';
import IconButton from '@material-ui/core/IconButton';

const HiddenIconButton = styled(IconButton)`
  && {
    visibility: hidden;
    opacity: 0;
    transition: ${props =>
      props.theme.transitions.create('opacity', {
        easing: props.theme.transitions.easing.sharp,
        duration: props.theme.transitions.duration.shortest,
      })};
  }
`;

const StyledFormControl = styled(FormControl)`
  && {
    &:hover {
      .inlineEditField-adornment {
        visibility: visible !important;
        opacity: 1;
      }
    }
  }
`;

const ArrayField = ({
  name,
  label,
  fields,
  onChange,
  value,
  canAddValues,
  canRemoveValues,
  register: shouldRegister,
  readOnly,
  onEdit,
  ...props
}) => {
  const intl = useIntl();
  const formContext = useFormContext();
  const {
    defaultValues = {},
    errors = {},
    editInline: formInline = false,
    isEditing: formIsEditing = true,
    setIsEditing,
  } = formContext || {};
  const fieldValues = fields.reduce(
    (acc, field) => ({
      ...acc,
      [field.name]: field.defaultValue,
    }),
    {},
  );
  const { inline = formInline, isEditing = formIsEditing } = props;
  const isInline = inline && !isEditing;
  const canAdd = canAddValues && !isInline;
  const canRemove = canRemoveValues && !isInline;

  useRegisterInput(name, shouldRegister);

  const { data, append, remove, update } = useArrayField({
    name,
    value: value ?? defaultValues?.[name],
    onChange,
  });

  const showEditAdornment = isInline && !readOnly;

  /**
   * On edit handler.
   */
  const handleOnEdit = () => {
    if (formContext && !onEdit) {
      setIsEditing(true);
    }

    if (onEdit) {
      onEdit();
    }
  };

  return (
    <StyledFormControl component="fieldset" variant="outlined" margin="normal" fullWidth>
      <Box display="flex" justifyContent="space-between" alignItems="center" height={48}>
        <FormLabel component="legend">{label ?? name}</FormLabel>
        {showEditAdornment && (
          <HiddenIconButton className="inlineEditField-adornment" onClick={handleOnEdit} edge="end">
            <Icon icon={['fal', 'pencil']} fontSize="small" />
          </HiddenIconButton>
        )}
      </Box>
      {isInline && data?.length === 0 && (
        <TextField name={name} value="-" readOnly={readOnly} inline />
      )}

      {data.map((dataObject, idx) => (
        // eslint-disable-next-line no-underscore-dangle
        <Box key={`${name}_field_${dataObject._id}`} py={1}>
          <Card variant="outlined">
            <CardContent>
              {fields.map(({ key, InputComponent, inputProps, name: subName }) => {
                const subErrors = errors?.[name]?.[idx];

                return (
                  <InputComponent
                    {...inputProps}
                    key={key}
                    name={`${name}_${idx}_${subName}`}
                    value={dataObject[subName]}
                    onChange={update(idx, subName)}
                    register={false}
                    error={!!subErrors?.[subName]}
                    errorMessage={
                      subErrors ? <ErrorMessage name={subName} errors={subErrors} /> : undefined
                    }
                  />
                );
              })}
              {canRemove && (
                <Button color="secondary" onClick={() => remove(idx)}>
                  {intl.formatMessage(translations.remove)}
                </Button>
              )}
            </CardContent>
          </Card>
        </Box>
      ))}
      {canAdd && (
        <Button color="primary" onClick={() => append(fieldValues)}>
          {intl.formatMessage(translations.add)}
        </Button>
      )}
      {errors[name]?.message && (
        <FormHelperText error>
          <ErrorMessage name={name} errors={errors} />
        </FormHelperText>
      )}
    </StyledFormControl>
  );
};

ArrayField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  fields: PropTypes.array,
  inline: PropTypes.bool,
  isEditing: PropTypes.bool,
  onChange: PropTypes.func,
  value: PropTypes.any,
  canAddValues: PropTypes.bool,
  canRemoveValues: PropTypes.bool,
  onEdit: PropTypes.func,
  readOnly: PropTypes.bool,
  register: PropTypes.bool,
};

ArrayField.defaultProps = {
  label: undefined,
  fields: [],
  inline: undefined,
  isEditing: undefined,
  onChange: undefined,
  value: undefined,
  canAddValues: true,
  canRemoveValues: true,
  onEdit: undefined,
  readOnly: false,
  register: true,
};

export default ArrayField;
